import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';

import { useTradeBenefitsContact } from '../../useTradeBenefitsContact.ts';
import { trackTradeBenefitsEvent } from '../../tradeBenefitsTracking.ts';

import { type TradeBenefitsYourFirmRep_viewer$key } from './__generated__/TradeBenefitsYourFirmRep_viewer.graphql.ts';

import styles from './TradeBenefitsYourFirmRep.scss';
import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment TradeBenefitsYourFirmRep_viewer on Viewer {
        ...useTradeBenefitsContact_viewer
        user(userId: $userId) {
            tradeFirm {
                ...useTradeBenefitsContact_tradeFirm
            }
        }
    }
`;

const TradeBenefitsYourFirmRep: FC<{ viewer: TradeBenefitsYourFirmRep_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const tradeFirm = viewer?.user?.tradeFirm;

    const { isAssigned, repEmail, photoUrl, displayName, phoneNumber } = useTradeBenefitsContact({
        viewer,
        tradeFirm,
    });

    if (isAssigned) {
        return (
            <div
                className={classnames(
                    styles.wrapper,
                    dibsCss.flex,
                    dibsCss.flexCol,
                    dibsCss.gapSmall
                )}
            >
                <div className={dibsCss.textBodyLargeHeavy}>
                    <FormattedMessage
                        id="abt.TradeBenefitsYourFirmRep.assigned.title"
                        defaultMessage="Your Dedicated Account Representative"
                    />
                </div>
                {photoUrl && (
                    <div
                        className={classnames(
                            styles.imageWrapper,
                            dibsCss.roundedFull,
                            dibsCss.overflowHidden
                        )}
                    >
                        <img
                            src={photoUrl}
                            alt={displayName || ''}
                            className={classnames(
                                dibsCss.hFull,
                                dibsCss.wFull,
                                dibsCss.objectCover
                            )}
                        />
                    </div>
                )}
                <div
                    className={classnames(
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.gapXsmall,
                        dibsCss.textBodyMediumNormal
                    )}
                >
                    {displayName}
                    <Link
                        href={`tel:${phoneNumber}`}
                        underline="none"
                        onClick={() =>
                            trackTradeBenefitsEvent({
                                stepInteractionName: 'phone clicked',
                                trigger: 'firm overview',
                            })
                        }
                    >
                        {phoneNumber}
                    </Link>
                    <Link
                        href={`mailto:${repEmail}`}
                        onClick={() =>
                            trackTradeBenefitsEvent({
                                stepInteractionName: 'email clicked',
                                trigger: 'firm overview',
                            })
                        }
                    >
                        {repEmail}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className={dibsCss.textBodyLargeHeavy}>
                <FormattedMessage
                    id="abt.TradeBenefitsYourFirmRep.unassigned.title"
                    defaultMessage="Trade Customer Support"
                />
            </div>
            {phoneNumber && (
                <div className={dibsCss.textBodyLargeNormal}>
                    <Link href={`tel:${phoneNumber}`} underline="none">
                        {phoneNumber}
                    </Link>
                </div>
            )}
            <Button
                href="/contact/"
                target="_blank"
                style="circle"
                type="standard"
                className={classnames(styles.button, dibsCss.mtSmall)}
                onClick={() =>
                    trackTradeBenefitsEvent({
                        stepInteractionName: 'contact clicked',
                        trigger: 'firm overview',
                    })
                }
            >
                <FormattedMessage
                    id="abt.TradeBenefitsYourFirmRep.unassigned.button"
                    defaultMessage="Contact"
                />
            </Button>
        </div>
    );
};

export { TradeBenefitsYourFirmRep };
